import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListSalesCampaignsInput } from "@nexxt/contracts/booking";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";
import { bookingQueries } from "../booking/booking.queries";

// Server state of the sales campaigns (50-api/booking.md §2.1): the list by page, one campaign, and the campaigns a
// form or a filter offers.

const campaigns = api.booking["sales-campaigns"];

type ListPage = InferResponseType<typeof campaigns.$get, 200>;
export type SalesCampaign = ListPage["items"][number];

/** The filters of the list input (`listSalesCampaignsInput`), without the paging the query adds. */
export type CampaignFilters = Pick<ListSalesCampaignsInput, "q" | "season"> & { archived?: boolean | undefined };

const PAGE_SIZE = 50;
/** As many as a select offers: the campaigns not archived are a few per season. */
const CHOICES = 100;

const list = (filters: CampaignFilters, page: number, pageSize: number, signal: AbortSignal) =>
  unwrap(
    campaigns.$get(
      {
        query: { ...filterQuery(filters), page: String(page), pageSize: String(pageSize) },
      },
      { init: { signal } },
    ),
  );

const keys = {
  all: [...bookingQueries.all, "campaigns"] as const,
  lists: [...bookingQueries.all, "campaigns", "list"] as const,
  details: [...bookingQueries.all, "campaigns", "detail"] as const,
};

export const campaignQueries = {
  ...keys,

  list: (filters: CampaignFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) => list(filters, pageParam, PAGE_SIZE, signal),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  detail: (salesCampaignId: string) =>
    queryOptions({
      queryKey: [...keys.details, salesCampaignId],
      queryFn: ({ signal }) =>
        unwrap(campaigns[":salesCampaignId"].$get({ param: { salesCampaignId } }, { init: { signal } })),
    }),

  /** The campaigns not archived, the one that starts last first: what a filter or the booking form offers. */
  choices: () =>
    queryOptions({
      queryKey: [...keys.all, "choices"],
      queryFn: async ({ signal }) => (await list({}, 1, CHOICES, signal)).items,
    }),
};
